import React, { useState } from 'react';
import {
  Download,
  CheckCircle2,
  MoreHorizontal,
  Trash2,
  Tag as TagIcon,
  X,
  Pencil,
} from 'lucide-react';
import { parseISO } from 'date-fns';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { ProductAsset, productService } from '@/services/productService';
import { TagInput } from '@/components/ui/tag-input';
import { Input } from '@/components/ui/input';
import { formatFileSize } from '@/utils/formatFileSize';
import { formatDateForDisplay, DateFormat } from '@/utils/numberFormat';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import { useProductDetailPageTranslations } from '@/hooks/useProductDetailPageTranslations';
import { OptimizedImage } from '@/components/ui/OptimizedImage';
import { PermissionGuard } from '@/components/common/PermissionGuard';
import { logDebug, logError } from '@/utils/errorHandler';

// Local implementation of asset type detection to avoid circular dependencies
const localAssetTypeService = {
  isImageAsset(asset: any): boolean {
    if (!asset) return false;

    // Check for content_type, asset_type, or type fields
    const type = asset.content_type || asset.asset_type || asset.type || '';
    if (type && type.toLowerCase().startsWith('image/')) {
      return true;
    }

    // Also check for type detection based on the detected asset type
    if (type && type.toLowerCase() === 'image') {
      return true;
    }

    // Check URL extension if available (for both url and file_url fields)
    const url = asset.url || asset.file_url || asset.file || '';
    if (url && typeof url === 'string') {
      const extension = url.split('.').pop()?.toLowerCase() || '';
      return [
        'jpg',
        'jpeg',
        'png',
        'gif',
        'svg',
        'webp',
        'bmp',
        'tiff',
        'ico',
        'heic',
        'avif',
      ].includes(extension);
    }

    return false;
  },
};

interface AssetCardProps {
  asset: ProductAsset;
  productId: number;
  onAssetUpdated: (updatedAsset: ProductAsset) => void;
  onMakePrimary: (asset: ProductAsset) => void;
  onDelete: (assetId: number) => void;
  onDownload: (asset: ProductAsset) => void;
  isImageAsset?: (asset?: ProductAsset) => boolean;
  getAssetIcon: (type: string) => React.ReactNode;
  getFileType: (asset: ProductAsset) => string;
  onRename?: (asset: ProductAsset) => void;
  isSelected?: boolean;
  onSelect?: (assetId: number) => void;
}

export function AssetCard({
  asset,
  productId,
  onAssetUpdated,
  onMakePrimary,
  onDelete,
  onDownload,
  isImageAsset,
  getAssetIcon,
  getFileType,
  onRename,
  isSelected = false,
  onSelect,
}: AssetCardProps) {
  const [isEditingTags, setIsEditingTags] = useState(false);
  const [tags, setTags] = useState<string[]>(Array.isArray(asset.tags) ? [...asset.tags] : []);
  const [inputValue, setInputValue] = useState('');
  const { getAssetsText } = useProductDetailPageTranslations();

  // Get organization settings for date formatting
  const { orgSettings } = useOrgSettings();
  const dateFormat = (orgSettings?.date_format || 'MDY') as DateFormat;

  // Use the provided isImageAsset function or fall back to our local implementation
  const checkIsImageAsset = (asset?: ProductAsset): boolean => {
    if (!asset) return false;

    // If parent component provided this function, use it
    if (isImageAsset) {
      return isImageAsset(asset);
    }

    // Otherwise use our local implementation
    return localAssetTypeService.isImageAsset(asset);
  };

  const handleUpdateTags = async () => {
    try {
      // Only proceed if we have actual tags to update or if we're removing all tags
      logDebug('Updating asset tags', {
        assetId: asset.id,
        newTags: tags,
        originalTags: asset.tags,
      });

      // Make a deep copy of the asset with updated tags to use for optimistic update
      const updatedAssetPreview = {
        ...asset,
        tags: [...tags], // Ensure we have a clean copy
      };

      // Immediately update the display (optimistic update)
      logDebug('Applying optimistic tag update', {
        assetId: asset.id,
        previewTags: updatedAssetPreview.tags,
      });
      onAssetUpdated(updatedAssetPreview);

      // Call the API
      logDebug('Calling updateAssetTags API', {
        productId,
        assetId: asset.id,
        tags: [...tags],
      });
      const updatedAsset = await productService.updateAssetTags(productId, asset.id, [...tags]);

      // Update again with the response from the server
      logDebug('Received API response for updateAssetTags', {
        assetId: asset.id,
        updatedTags: updatedAsset.tags,
      });
      onAssetUpdated(updatedAsset);

      // Exit edit mode
      setIsEditingTags(false);
      setInputValue('');
    } catch (error) {
      logError(
        'Failed to update asset tags',
        {
          component: 'AssetCard',
          action: 'handleUpdateTags',
          metadata: { assetId: asset.id, productId, error },
        },
        {
          showToast: true,
          userFacing: true,
        }
      );
      // If there's an error, fall back to the original tags
      setTags(Array.isArray(asset.tags) ? [...asset.tags] : []);
    }
  };

  // Ensure tags is always an array
  const assetTags = Array.isArray(asset.tags) ? asset.tags : [];

  const formatAssetDate = (dateString: string): string => {
    try {
      const dateValue = parseISO(dateString);
      return formatDateForDisplay(dateValue, dateFormat);
    } catch {
      return getAssetsText('assetCard.unknownDate');
    }
  };

  return (
    <Card
      className={cn(
        'overflow-hidden group rounded-md shadow-sm min-w-0 relative',
        asset.is_primary && checkIsImageAsset(asset) && 'ring-1 ring-primary',
        isSelected && 'ring-2 ring-primary bg-primary/5 border-primary border-2'
      )}
    >
      {/* Multi-select checkbox */}
      {typeof isSelected === 'boolean' && onSelect && (
        <div className="absolute left-2 top-2 z-10">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onSelect(asset.id)}
            aria-label={
              isSelected
                ? getAssetsText('assetCard.deselectAsset')
                : getAssetsText('assetCard.selectAsset')
            }
            className="size-4 rounded border border-primary focus:ring-2 focus:ring-primary"
          />
        </div>
      )}
      <div className="relative p-1 pb-0">
        {/* Asset preview */}
        <div className="m-0 flex aspect-[4/3] items-center justify-center overflow-hidden rounded-md bg-muted p-0">
          {checkIsImageAsset(asset) ? (
            (() => {
              logDebug('Rendering OptimizedImage for asset', {
                component: 'AssetCard',
                action: 'renderOptimizedImage',
                metadata: {
                  assetId: asset.id,
                  assetName: asset.name,
                  assetType: asset.asset_type || asset.type,
                },
              });
              return (
                <OptimizedImage
                  asset={asset}
                  alt={asset.name || getAssetsText('assetCard.unnamedAsset')}
                  context="gallery"
                  className="size-full object-cover"
                />
              );
            })()
          ) : (
            <div className="flex h-full flex-col items-center justify-center p-3 text-center">
              {getAssetIcon(asset.type || 'unknown')}
              <p className="mt-1 text-xs font-medium text-muted-foreground">
                {getFileType(asset)?.toUpperCase() || getAssetsText('assetCard.unknown')}
              </p>
              <p className="mt-1 max-w-full truncate px-2 text-xs">
                {asset.name || getAssetsText('assetCard.unnamed')}
              </p>
            </div>
          )}
        </div>
      </div>

      <CardContent className="space-y-2 p-3 pt-2">
        <div className="flex items-start justify-between gap-1">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1">
              {getAssetIcon(asset.type || 'unknown')}
              <h3 className="truncate text-xs font-medium">
                {asset.name || getAssetsText('assetCard.unnamed')}
              </h3>
            </div>

            {/* Tags display or editor */}
            <div className="mt-1">
              {isEditingTags ? (
                <div className="mb-2">
                  <TagInput
                    id={`asset-tag-editor-${asset.id}`}
                    tags={tags}
                    setTags={setTags}
                    placeholder={getAssetsText('assetCard.addTags')}
                    maxTags={10}
                  />

                  {/* Add a dedicated Add Tag button */}
                  <div className="mb-1 mt-2 flex items-center">
                    <Input
                      type="text"
                      placeholder={getAssetsText('assetCard.addNewTag')}
                      className="h-6 flex-1 px-2 text-xs"
                      value={inputValue}
                      onChange={e => setInputValue(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && inputValue.trim()) {
                          e.preventDefault();
                          logDebug('Adding tag from explicit input', {
                            component: 'AssetCard',
                            action: 'addTagFromInput',
                            metadata: { assetId: asset.id, inputValue },
                          });
                          setTags([...tags, inputValue.trim()]);
                          setInputValue('');
                        }
                      }}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="ml-1 h-6 px-2 text-xs"
                      onClick={() => {
                        if (inputValue.trim()) {
                          logDebug('Adding tag from explicit button', {
                            component: 'AssetCard',
                            action: 'addTagFromButton',
                            metadata: { assetId: asset.id, inputValue },
                          });
                          setTags([...tags, inputValue.trim()]);
                          setInputValue('');
                        }
                      }}
                      disabled={!inputValue.trim()}
                    >
                      {getAssetsText('assetCard.addTagButton')}
                    </Button>
                  </div>

                  <div className="mt-1 flex justify-end gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-xs"
                      onClick={() => {
                        // Reset to original tags and exit edit mode
                        setTags(Array.isArray(asset.tags) ? [...asset.tags] : []);
                        setInputValue('');
                        setIsEditingTags(false);
                      }}
                    >
                      <X className="mr-1 size-3" />
                      {getAssetsText('assetCard.buttons.cancel')}
                    </Button>
                    <Button
                      size="sm"
                      variant="primary"
                      className="h-6 px-2 text-xs"
                      onClick={() => {
                        logDebug('Save button clicked', {
                          component: 'AssetCard',
                          action: 'saveTags',
                          metadata: { assetId: asset.id, tagsCount: tags.length },
                        });
                        handleUpdateTags();
                      }}
                    >
                      {getAssetsText('assetCard.buttons.save')}{' '}
                      {tags.length > 0 ? `(${tags.length})` : ''}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-1 flex flex-wrap gap-1">
                  {assetTags.length > 0 ? (
                    assetTags.map((tag, index) => (
                      <Badge
                        key={`${tag}-${index}`}
                        variant="outline"
                        className="h-4 bg-secondary/20 px-1 text-[10px]"
                      >
                        {tag}
                      </Badge>
                    ))
                  ) : (
                    <PermissionGuard
                      permission="product.change"
                      fallback={
                        <span className="text-[10px] italic text-muted-foreground">
                          {getAssetsText('assetCard.noTags').replace('(click to add)', '')}
                        </span>
                      }
                    >
                      <span
                        className="text-[10px] italic text-muted-foreground"
                        onClick={() => {
                          setIsEditingTags(true);
                        }}
                      >
                        {getAssetsText('assetCard.noTags')}
                      </span>
                    </PermissionGuard>
                  )}
                </div>
              )}
            </div>

            <div className="mt-1 space-y-1 text-xs text-muted-foreground">
              <p className="text-[10px]">
                {getAssetsText('assetCard.sizeLabel')}: {formatFileSize(asset.size)}
              </p>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px]">
                  {asset.uploaded_at
                    ? formatAssetDate(asset.uploaded_at)
                    : getAssetsText('assetCard.unknownDate')}
                </span>
                <div className="flex flex-1 items-center justify-end">
                  {/* Primary image badge or button */}
                  {asset.is_primary && checkIsImageAsset(asset) ? (
                    <Badge
                      variant="outline"
                      className="h-4 border-primary/20 bg-primary/10 px-1 text-[10px] text-primary"
                    >
                      <CheckCircle2 className="mr-0.5 size-2" />
                      {getAssetsText('assetCard.primaryBadge')}
                    </Badge>
                  ) : checkIsImageAsset(asset) ? (
                    <PermissionGuard permission="product.change">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-auto h-4 px-1 py-0 text-[10px] hover:bg-primary/10 hover:text-primary"
                        onClick={() => onMakePrimary(asset)}
                      >
                        {getAssetsText('assetCard.setAsPrimary')}
                      </Button>
                    </PermissionGuard>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          {/* Asset dropdown menu */}
          <PermissionGuard permission="product.change">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="size-6">
                  <MoreHorizontal className="size-3" />
                  <span className="sr-only">{getAssetsText('assetCard.assetMenu')}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-36">
                <DropdownMenuItem onClick={() => onDownload(asset)} className="text-xs">
                  <Download className="mr-2 size-3" />
                  {getAssetsText('assetCard.buttons.download')}
                </DropdownMenuItem>
                {/* Rename option */}
                {onRename && (
                  <DropdownMenuItem onClick={() => onRename(asset)} className="text-xs">
                    <Pencil className="mr-2 size-3" />
                    {getAssetsText('assetCard.buttons.rename')}
                  </DropdownMenuItem>
                )}
                {/* Only show Set as Primary for images that aren't already primary */}
                {!asset.is_primary && checkIsImageAsset(asset) && (
                  <DropdownMenuItem onClick={() => onMakePrimary(asset)} className="text-xs">
                    <CheckCircle2 className="mr-2 size-3" />
                    {getAssetsText('assetCard.setAsPrimary')}
                  </DropdownMenuItem>
                )}
                {/* Edit Tags option */}
                <DropdownMenuItem
                  onClick={() => {
                    setTags(Array.isArray(asset.tags) ? [...asset.tags] : []);
                    setIsEditingTags(true);
                  }}
                  className="text-xs"
                >
                  <TagIcon className="mr-2 size-3" />
                  {getAssetsText('assetCard.buttons.editTags')}
                </DropdownMenuItem>
                {/* Delete option - placed last */}
                <DropdownMenuItem onClick={() => onDelete(asset.id)} className="text-xs">
                  <Trash2 className="mr-2 size-3" />
                  {getAssetsText('assetCard.buttons.delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </PermissionGuard>
        </div>
      </CardContent>
    </Card>
  );
}
